import React, { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Button } from '@mui/material';

import { OverviewMode } from '../types/Scan';

interface Props {
    mode: OverviewMode;
    filterAction: (filterCriteria: string, filterId: string) => void;
    destinations: Array<string>;
    initialFilterCriteria: string;
    initialFilterId: string;
}

const FilterScanningList = ({
    mode,
    filterAction,
    initialFilterCriteria,
    initialFilterId,
    destinations
}: Props): ReactElement => {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const initialMount = useRef<boolean>(true);
    const [filterCriteria, setFilterCriteria] = useState('');
    const [filterId, setFilterId] = useState('');
    const [destinationOptions, setDestinationOptions] = useState<Array<{ value: string; label: string }>>([]);

    useEffect(() => {
        setFilterCriteria(initialFilterCriteria ? initialFilterCriteria : '');
        setFilterId(initialFilterId ? initialFilterId : '');
    }, [initialFilterCriteria, initialFilterId]);

    useEffect(() => {
        if (filterCriteria !== 'otherLocationName' && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [filterCriteria]);

    useEffect(() => {
        if (destinations) {
            setDestinationOptions(
                [{ value: '', label: '- Please Select -' }].concat(
                    destinations.map((destination: string) => ({
                        value: destination,
                        label: destination
                    }))
                )
            );
        }
    }, [destinations]);

    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }
        setFilterCriteria('');
        setFilterId('');
    }, [mode]);

    const filterScans = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        filterAction(filterCriteria, filterId);
    };

    const clearScan = () => {
        filterAction('', '');
        setFilterId('');
        setFilterCriteria('');
    };

    return (
        <form data-testid="filter-scanning-list" onSubmit={(e) => filterScans(e)} className="flex-gap">
            <select
                data-testid="filter-criteria"
                value={filterCriteria}
                onChange={(e) => {
                    setFilterCriteria(e.target.value);
                    setFilterId('');
                }}
            >
                <option value="" disabled>
                    Filter by
                </option>
                <option value="loggerId">Logger ID</option>
                <option value="trackingId">Tracking ID</option>
                <option value="salesOrderId">Sales order ID</option>
                {mode === 'SCANNING' && <option value="otherLocationName">Destination</option>}
            </select>

            {filterCriteria === 'otherLocationName' ? (
                <Select
                    id="destination-selector"
                    styles={{
                        input: (base: any): any => ({
                            ...base,
                            '[type="text"]': {
                                boxShadow: 'none'
                            }
                        }),
                        control: (provided) => ({
                            ...provided,
                            width: 200,
                            boxShadow: 'none',
                            minHeight: 36,
                            height: 36,
                            alignContent: 'center'
                        })
                    }}
                    value={destinationOptions.find((options: any) => options.value === filterId)}
                    onChange={(selected: any) => setFilterId(selected?.value)}
                    maxMenuHeight={230}
                    placeholder="- Please select -"
                    options={destinationOptions}
                />
            ) : (
                <input
                    data-testid="filter-id"
                    value={filterId}
                    onChange={(e) => setFilterId(e.target.value)}
                    ref={searchInputRef}
                    disabled={!filterCriteria}
                    type="text"
                    className="large"
                    placeholder="Please enter ID"
                />
            )}

            <Button variant="contained" data-testid="filter-btn" type="submit" disabled={!filterCriteria || !filterId}>
                Filter
            </Button>

            <Button
                variant="outlined"
                data-testid="clear-button"
                onClick={() => clearScan()}
                disabled={!filterCriteria}
            >
                Clear
            </Button>
        </form>
    );
};

export default FilterScanningList;
